import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useNotifications } from '../api/queries';
import { useSession } from '../auth/session';
import { roleLabel } from '../lib/format';

function navClass({ isActive }: { isActive: boolean }): string {
  return isActive ? 'nav__link nav__link--active' : 'nav__link';
}

/** Moves focus to the new page's main landmark after client-side navigation. */
function useFocusOnNavigate() {
  const location = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (location.hash !== '') return;
    const main = document.getElementById('main');
    if (main !== null) {
      main.focus({ preventScroll: true });
      window.scrollTo({ top: 0 });
    }
  }, [location.pathname, location.hash]);
}

export function Layout() {
  const session = useSession();
  const notifications = useNotifications();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useFocusOnNavigate();

  useEffect(() => setMenuOpen(false), [location.pathname]);

  const unread = notifications.data?.items.filter((item) => item.readAt === undefined || item.readAt === null).length ?? 0;
  const isSteward = session.hasRole('steward');

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className="masthead">
        <div className="masthead__inner">
          <Link to="/" className="masthead__brand" aria-label="GitFitCode research hub, report home">
            <img src="/brand/logo-long.png" alt="GitFitCode" width={801} height={295} className="masthead__logo" />
            <span className="masthead__product">Research hub</span>
          </Link>
          <button
            type="button"
            className="masthead__menu-button"
            aria-expanded={menuOpen}
            aria-controls="primary-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? 'Close' : 'Menu'}
          </button>
          <nav id="primary-nav" aria-label="Primary" className={menuOpen ? 'nav nav--open' : 'nav'}>
            <ul className="nav__list">
              <li>
                <NavLink to="/" end className={navClass}>
                  Report
                </NavLink>
              </li>
              <li>
                <NavLink to="/explore" className={navClass}>
                  Explore
                </NavLink>
              </li>
              <li>
                <NavLink to="/maps" className={navClass}>
                  Original maps
                </NavLink>
              </li>
              <li>
                <NavLink to="/pilots" className={navClass}>
                  Pilots
                </NavLink>
              </li>
              <li>
                <NavLink to="/participate" className={navClass}>
                  Participate
                  {unread > 0 && (
                    <span className="nav__count" aria-label={`${unread} new review notifications`}>
                      {unread}
                    </span>
                  )}
                </NavLink>
              </li>
              {isSteward && (
                <li>
                  <NavLink to="/steward" className={navClass}>
                    Stewardship
                  </NavLink>
                </li>
              )}
            </ul>
            <div className="nav__account">
              <span className="nav__who">
                {session.user.displayName}
                <span className="nav__role">{roleLabel[session.user.role]}</span>
              </span>
              <button type="button" className="button button--quiet" onClick={() => void session.signOut()}>
                Sign out
              </button>
            </div>
          </nav>
        </div>
      </header>
      <Outlet />
      <footer className="colophon">
        <div className="colophon__inner">
          <img src="/brand/logo-short.png" alt="" width={234} height={234} className="colophon__mark" />
          <p>
            A private, invited research space for GitFitCode members. Findings are drafted from the archive, measured by
            reproducible queries and published only after a steward reviews them. Every view checks your access and
            each person&apos;s attribution preference.
          </p>
        </div>
      </footer>
    </div>
  );
}
